import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { fetchAuthSession, signIn, signOut, confirmSignIn, rememberDevice } from '@aws-amplify/auth';

export interface AuthUser {
  sub: string;
  email: string;
  groups: string[];
}

export interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

export interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<{ challengeName?: string }>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  confirmTOTPCode: (code: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Phase 1: there is no live Cognito user pool yet. fetchAuthSession() (and any
  // other Amplify Auth call) will throw when Amplify hasn't been configured with
  // real pool IDs, or simply resolve with no tokens. Either way we must not let
  // that crash the app — just treat the visitor as logged out.
  const loadUser = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      const payload = session.tokens?.idToken?.payload;
      if (!payload) {
        setUser(null);
        return;
      }
      const rawGroups = payload['cognito:groups'] as string | string[] | undefined;
      const groups = Array.isArray(rawGroups)
        ? rawGroups
        : typeof rawGroups === 'string'
          ? rawGroups.split(',').map((g) => g.trim())
          : [];
      setUser({
        sub: (payload.sub as string) ?? '',
        email: (payload.email as string) ?? '',
        groups,
      });
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await signIn({ username: email, password });
      if (result.isSignedIn) {
        await rememberDevice().catch(() => {});
        await loadUser();
      }
      return { challengeName: result.nextStep?.signInStep };
    },
    [loadUser],
  );

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      await confirmSignIn({ challengeResponse: newPassword });
      await rememberDevice().catch(() => {});
      await loadUser();
    },
    [loadUser],
  );

  const confirmTOTPCode = useCallback(
    async (code: string) => {
      await confirmSignIn({ challengeResponse: code });
      await rememberDevice().catch(() => {});
      await loadUser();
    },
    [loadUser],
  );

  const logout = useCallback(async () => {
    try {
      await signOut();
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      isAdmin: user?.groups.includes('Admins') ?? false,
      login,
      completeNewPassword,
      confirmTOTPCode,
      logout,
    }),
    [user, isLoading, login, completeNewPassword, confirmTOTPCode, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
